import React from 'react';
import { Layout } from '../../components/layout/Layout';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPage = () => {
    return (
        <Layout>
            {/* Hero */}
            <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-primary-200 hover:text-white mb-6 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Home
                    </Link>
                    <div className="flex items-center gap-3 mb-4">
                        <Shield className="w-8 h-8" />
                        <h1 className="text-3xl sm:text-4xl font-bold">Privacy Policy</h1>
                    </div>
                    <p className="text-primary-200">Last updated: 16th January, 2026</p>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-lg max-w-none prose-headings:text-neutral-900 prose-p:text-neutral-700">
                        <p className="lead text-lg text-neutral-600 mb-8">
                            RecruitNearMe.com ("RecruitNearMe", "we", "us", or "our") is committed to protecting your privacy. 
                            This Privacy Policy explains how we collect, use, store, and protect your personal information when 
                            you use our website and services.
                        </p>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">1. Information We Collect</h2>
                        <p>We may collect the following information:</p>
                        
                        <h3 className="text-xl font-semibold mt-6 mb-3 text-neutral-800">a) Information You Provide</h3>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>Full name</li>
                            <li>Email address</li>
                            <li>Phone number (optional)</li>
                            <li>CV / resume and cover letters</li>
                            <li>Job preferences (location, role, salary expectations)</li>
                            <li>Account login details</li>
                            <li>Payment information (processed securely by third-party providers)</li>
                        </ul>

                        <h3 className="text-xl font-semibold mt-6 mb-3 text-neutral-800">b) Automatically Collected Information</h3>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>IP address</li>
                            <li>Browser type and device information</li>
                            <li>Pages visited and usage data</li>
                            <li>Cookies and similar technologies</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">2. How We Use Your Information</h2>
                        <p>We use your data to:</p>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>Provide access to recruiter listings near you</li>
                            <li>Submit CVs to recruiters (with your permission)</li>
                            <li>Deliver multi-apply and interview support services</li>
                            <li>Manage your account and subscriptions</li>
                            <li>Improve website functionality and user experience</li>
                            <li>Communicate updates, service notices, and support responses</li>
                            <li>Comply with legal and regulatory obligations</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">3. Sharing Your Information</h2>
                        <p><strong>We do not sell your personal data.</strong></p>
                        <p>We may share information with:</p>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>Recruiters you choose to contact or apply to</li>
                            <li>Service providers (e.g. hosting, payment processing, analytics)</li>
                            <li>Legal authorities where required by law</li>
                        </ul>
                        <p>All third parties are required to handle your data securely and lawfully.</p>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">4. Lawful Basis for Processing (UK GDPR)</h2>
                        <p>We process personal data based on:</p>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>Your consent</li>
                            <li>Contractual necessity (to deliver services)</li>
                            <li>Legitimate interests (improving services)</li>
                            <li>Legal obligations</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">5. Data Retention</h2>
                        <p>We retain your data only as long as necessary:</p>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li><strong>Active accounts:</strong> for the duration of service use</li>
                            <li><strong>Inactive accounts:</strong> up to 12–24 months unless requested otherwise</li>
                            <li><strong>Legal or compliance data:</strong> as required by law</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">6. Your Rights</h2>
                        <p>You have the right to:</p>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>Access your personal data</li>
                            <li>Correct inaccurate information</li>
                            <li>Request deletion of your data</li>
                            <li>Withdraw consent at any time</li>
                            <li>Object to certain processing</li>
                            <li>Lodge a complaint with the ICO (UK)</li>
                        </ul>
                        <p>Requests can be sent to: <span className="font-medium text-primary-600">support@recruitnearme.com</span></p>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">7. Cookies</h2>
                        <p>We use cookies to:</p>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>Improve site functionality</li>
                            <li>Analyse traffic and performance</li>
                            <li>Remember user preferences</li>
                        </ul>
                        <p>You can manage cookies through your browser settings or our cookie consent banner.</p>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">8. Data Security</h2>
                        <p>
                            We implement appropriate technical and organisational measures to protect your information 
                            from unauthorised access, loss, or misuse.
                        </p>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">9. Changes to This Policy</h2>
                        <p>
                            We may update this Privacy Policy from time to time. Updates will be posted on this page 
                            with a revised "Last updated" date.
                        </p>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">10. Contact Us</h2>
                        <p>For privacy-related enquiries:</p>
                        <p className="font-medium text-primary-600">privacy@recruitnearme.com</p>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default PrivacyPage;
